/**
 * Signal Details — read-only enriched view + convert-to-RNC.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, RefreshCw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { signalsApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { WebsiteLink } from "@/components/common/WebsiteLink";
import PropTypes from "prop-types";

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function Field({ label, value, website = false }) {
  const display = fmt(value);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".04em", color: "#94a3b8" }}>{label}</div>
      <div style={{ fontSize: 14, color: "#0f172a", marginTop: 2, whiteSpace: "pre-wrap" }}>
        {website && display !== "—" ? (
          <WebsiteLink
            value={value}
            style={{ color: "#4f46e5", textDecoration: "none" }}
            data-testid="signal-company-website"
          />
        ) : (
          display
        )}
      </div>
    </div>
  );
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  website: PropTypes.bool,
};

export default function SignalDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, signal: null, activity: [], error: null });
  const [reload, setReload] = useState(0);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.all([
      signalsApi.get(id),
      signalsApi.activity(id).catch(() => ({ activity: [] })),
    ])
      .then(([signal, act]) => {
        if (!cancelled) {
          setState({
            loading: false,
            signal,
            activity: act.activity || [],
            error: null,
          });
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setState({ loading: false, signal: null, activity: [], error: e.message || "Failed to load" });
        }
      });
    return () => { cancelled = true; };
  }, [id, reload]);

  async function convert() {
    setConverting(true);
    try {
      const res = await signalsApi.convertToRnc(id);
      toast.success(res.message || "Converted to RNC");
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(e.message || "Convert failed");
    } finally {
      setConverting(false);
    }
  }

  const sig = state.signal;
  const sa = sig?.suggested_action || {};

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-y-auto bg-white" data-testid="signal-detail-page">
      <div className="max-w-[900px] mx-auto w-full px-4 sm:px-8 py-6 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Button type="button" variant="outline" size="sm" onClick={() => navigate("/signals")} data-testid="signal-detail-back">
            <ArrowLeft size={14} /> Back
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setReload((n) => n + 1)}>
            <RefreshCw size={14} /> Refresh
          </Button>
          {sig?.external_url && (
            <a href={sig.external_url} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 inline-flex items-center gap-1">
              <ExternalLink size={13} /> Open source
            </a>
          )}
          {sig && sig.status !== "converted" && (
            <>
              {sig.status !== "approved" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const res = await signalsApi.updateStatus(id, "approved");
                      if (res.auto_convert?.ok) {
                        toast.success(res.auto_convert.message || "Approved and converted to RNC");
                      } else {
                        toast.success("Signal approved");
                      }
                      setReload((n) => n + 1);
                    } catch (e) {
                      toast.error(e.message || "Approve failed");
                    }
                  }}
                  data-testid="signal-approve"
                >
                  Approve
                </Button>
              )}
              <Button type="button" size="sm" onClick={convert} disabled={converting} data-testid="signal-convert-rnc">
                {converting ? "Converting…" : "Convert to RNC"}
              </Button>
            </>
          )}
        </div>

        {state.loading && (
          <div className="space-y-3">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}

        {!state.loading && state.error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-rose-700 text-sm">
            {state.error}
            <div className="mt-2"><Link to="/signals" className="text-indigo-600">Return to Signals</Link></div>
          </div>
        )}

        {!state.loading && sig && (
          <>
            <header>
              <h1 className="text-2xl font-semibold text-slate-900">
                {sig.company_name || sig.title || "Signal"}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {(sig.source || "").replace("_group", "")} · {sig.status} · score {Math.round((sig.confidence ?? sig.relevance_score ?? 0) * 100)}%
              </p>
            </header>

            <section className="rounded-xl border border-slate-200 p-4 space-y-2">
              <h2 className="text-sm font-semibold text-slate-800">Original content</h2>
              <p className="text-[13.5px] text-slate-700 whitespace-pre-wrap">{fmt(sig.body_text)}</p>
            </section>

            <section className="rounded-xl border border-slate-200 p-4 grid sm:grid-cols-2 gap-3">
              <Field label="AI SUMMARY" value={sig.ai_summary || sa.why} />
              <Field label="AI REASONING" value={(sig.match_reasons || []).join(", ")} />
              <Field label="INTENT" value={sig.intent_tag} />
              <Field label="SUGGESTED ACTION" value={sa.primary?.kind} />
              <Field label="DRAFT" value={sa.primary?.draft} />
            </section>

            <section className="rounded-xl border border-slate-200 p-4 grid sm:grid-cols-2 gap-3">
              <Field label="COMPANY" value={sig.company_name} />
              <Field label="CONTACT" value={sig.contact_name || sig.author_name || sig.author_handle} />
              <Field label="EMAIL" value={sig.contact_email} />
              <Field label="PHONE" value={sig.contact_phone} />
              <Field label="CITY" value={sig.company_city} />
              <Field label="COUNTRY" value={sig.company_country} />
              <Field label="INDUSTRY" value={sig.company_industry} />
              <Field label="WEBSITE" value={sig.company_website} website />
            </section>

            <section className="rounded-xl border border-slate-200 p-4">
              <h2 className="text-sm font-semibold text-slate-800 mb-3">Activity timeline</h2>
              {!state.activity.length ? (
                <p className="text-sm text-slate-400">No activity recorded yet.</p>
              ) : (
                <ul className="space-y-2">
                  {state.activity.map((a) => (
                    <li key={a.id} className="text-[13px] text-slate-700 border-b border-slate-100 pb-2">
                      <span className="font-medium">{a.event_type}</span>
                      <span className="text-slate-400"> · {formatDateTime(a.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
